import { ENCRYPTION_KEY, CURRENT_SERVER } from './ApiConstants';
import CryptoJS from 'crypto-js';

/**
 * Fixed EncryptedStorage - All writes are now immediate by default
 */
class EncryptedStorage {
  static cache = new Map();
  static cacheExpiry = new Map();
  static defaultCacheTTL = 5 * 60 * 1000; // 5 minutes

  static storageEventListenerAdded = false;

  static perfStats = {
    cacheHits: 0,
    cacheMisses: 0,
    encryptOps: 0,
    decryptOps: 0,
    writeOps: 0,
    readOps: 0
  };

  /**
   * Initialize storage event listener for cross-tab sync
   */
  static initStorageListener() {
    if (this.storageEventListenerAdded || typeof window === 'undefined') {
      return;
    }

    window.addEventListener('storage', (event) => {
      if (event.key === 'rememberMe' && event.newValue !== event.oldValue) {
        console.log('RememberMe changed in another tab');
        this.clearCache();
        window.dispatchEvent(new CustomEvent('storage-rememberme-changed', {
          detail: { newValue: event.newValue, oldValue: event.oldValue }
        }));
      }

      if (event.key === '_storage_sync_signal_') {
        this.clearCache();
      }

      // Invalidate cache for changed key
      if (event.key && event.key !== '_storage_sync_signal_') {
        this.cache.delete(event.key);
        this.cacheExpiry.delete(event.key);
      }
    });

    this.storageEventListenerAdded = true;
  }

  /**
   * Check if encryption should be used
   */
  static shouldEncrypt() {
    // Encryption temporarily disabled.
    // return !['SMDEV', 'SMSTG']?.includes(CURRENT_SERVER);
    return false;
  }

  /**
   * Get the appropriate storage based on rememberMe flag
   */
  static getStorage() {
    // If this session is explicitly bound to session storage, or contains the auth token, use it.
    if (sessionStorage.getItem('_FORCE_SESSION_') === 'true' || sessionStorage.getItem('_UFT_')) {
      return sessionStorage;
    }

    const rememberMe = localStorage?.getItem('rememberMe');
    const shouldRemember = rememberMe === 'true' || rememberMe === true;
    return shouldRemember ? localStorage : sessionStorage;
  }

  /**
   * Optimized encryption with type checking
   */
  static encrypt(data) {
    try {
      this.perfStats.encryptOps++;

      const stringData = typeof data === 'string' ? data : JSON.stringify(data);

      if (!this.shouldEncrypt()) {
        return stringData;
      }

      return CryptoJS.AES.encrypt(stringData, ENCRYPTION_KEY).toString();
    } catch (error) {
      console.error('Encryption failed:', error);
      throw new Error('Failed to encrypt data');
    }
  }

  /**
   * Optimized decryption with caching
   */
  static decrypt(encryptedData) {
    try {
      this.perfStats.decryptOps++;

      if (!this.shouldEncrypt()) {
        try {
          return JSON.parse(encryptedData);
        } catch {
          return encryptedData;
        }
      }

      const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
      const decryptedString = bytes.toString(CryptoJS.enc.Utf8);

      if (!decryptedString) {
        throw new Error('Failed to decrypt - invalid key or corrupted data');
      }

      try {
        return JSON.parse(decryptedString);
      } catch {
        return decryptedString;
      }
    } catch (error) {
      console.error('Decryption failed:', error);
      throw new Error('Failed to decrypt data');
    }
  }

  /**
   * Check if cache entry is still valid
   */
  static isCacheValid(key) {
    const expiry = this.cacheExpiry.get(key);
    return expiry && Date.now() < expiry;
  }

  /**
   * Clear expired cache entries
   */
  static clearExpiredCache() {
    const now = Date.now();
    const expiredKeys = [];

    for (const [key, expiry] of this.cacheExpiry.entries()) {
      if (now >= expiry) {
        expiredKeys.push(key);
      }
    }

    expiredKeys.forEach(key => {
      this.cache.delete(key);
      this.cacheExpiry.delete(key);
    });
  }

  /**
   * Set encrypted item with IMMEDIATE writes by default (FIXED)
   * @param {string} key - Storage key
   * @param {any} value - Value to store
   * @param {number} cacheTTL - Cache time-to-live
   * @param {boolean} immediate - Force immediate write (kept for compatibility but now default is true)
   */
  static setItem(key, value, cacheTTL = this.defaultCacheTTL, immediate = true) {
    try {
      const processedValue = this.shouldEncrypt()
        ? this.encrypt(value)
        : (typeof value === 'string' ? value : JSON.stringify(value));

      // Update cache immediately
      this.cache.set(key, value);
      this.cacheExpiry.set(key, Date.now() + cacheTTL);

      // FIXED: Always write immediately to storage
      const storage = this.getStorage();
      storage?.setItem(key, processedValue);
      this.perfStats.writeOps++;

      return true;
    } catch (error) {
      console.error('Failed to set item:', error);
      return false;
    }
  }

  /**
   * Optimized get with smart caching
   */
  static getItem(key, bypassCache = false) {
    try {
      this.perfStats.readOps++;

      // Check cache first
      if (!bypassCache && this.isCacheValid(key)) {
        this.perfStats.cacheHits++;
        return this.cache.get(key);
      }

      this.perfStats.cacheMisses++;

      const storage = this.getStorage();
      const storedValue = storage?.getItem(key);

      if (storedValue === null) {
        this.cache.delete(key);
        this.cacheExpiry.delete(key);
        return null;
      }

      if (['SMDEV', 'SMSTG']?.includes(CURRENT_SERVER)) {
        this.cache.set(key, storedValue);
        this.cacheExpiry.set(key, Date.now() + this.defaultCacheTTL);
        return storedValue;
      }

      const decryptedValue = this.decrypt(storedValue);

      // Update cache
      this.cache.set(key, decryptedValue);
      this.cacheExpiry.set(key, Date.now() + this.defaultCacheTTL);

      return decryptedValue;
    } catch (error) {
      console.error('Failed to get item:', error);
      this.cache.delete(key);
      this.cacheExpiry.delete(key);
      return null;
    }
  }

  /**
   * Optimized batch get with parallel processing
   */
  static getMultipleItems(keys) {
    const result = {};
    const keysToFetch = [];

    // Separate cached vs non-cached
    for (const key of keys) {
      if (this.isCacheValid(key)) {
        result[key] = this.cache.get(key);
        this.perfStats.cacheHits++;
      } else {
        keysToFetch.push(key);
      }
    }

    // Batch fetch from storage
    if (keysToFetch.length > 0) {
      const storage = this.getStorage();

      for (const key of keysToFetch) {
        const storedValue = storage?.getItem(key);
        if (storedValue !== null) {
          try {
            const decryptedValue = this.shouldEncrypt()
              ? this.decrypt(storedValue)
              : storedValue;

            result[key] = decryptedValue;
            this.cache.set(key, decryptedValue);
            this.cacheExpiry.set(key, Date.now() + this.defaultCacheTTL);
          } catch (error) {
            console.error(`Failed to decrypt key ${key}:`, error);
            result[key] = null;
          }
        } else {
          result[key] = null;
        }
        this.perfStats.cacheMisses++;
      }
    }

    return result;
  }

  /**
   * Batch set with immediate writes (FIXED)
   */
  static setMultipleItems(items, cacheTTL = this.defaultCacheTTL) {
    const results = {};
    const storage = this.getStorage();

    for (const [key, value] of Object.entries(items)) {
      try {
        const processedValue = this.shouldEncrypt()
          ? this.encrypt(value)
          : (typeof value === 'string' ? value : JSON.stringify(value));

        // Update cache
        this.cache.set(key, value);
        this.cacheExpiry.set(key, Date.now() + cacheTTL);

        // FIXED: Write immediately
        storage?.setItem(key, processedValue);
        this.perfStats.writeOps++;

        results[key] = true;
      } catch (error) {
        console.error(`Failed to process key ${key}:`, error);
        results[key] = false;
      }
    }

    return results;
  }

  /**
   * Remove item from storage and cache
   */
  static removeItem(key) {
    const storage = this.getStorage();
    storage?.removeItem(key);

    // Clear cache
    this.cache.delete(key);
    this.cacheExpiry.delete(key);
  }

  /**
   * Clear all items
   */
  static clear() {
    localStorage.clear();
    sessionStorage.clear();
    this.cache.clear();
    this.cacheExpiry.clear();
  }

  /**
   * Clear only cache
   */
  static clearCache() {
    this.cache.clear();
    this.cacheExpiry.clear();
  }

  /**
   * Get all keys from current storage
   */
  static getAllKeys() {
    const storage = this.getStorage();
    const keys = [];
    for (let i = 0; i < storage.length; i++) {
      keys.push(storage.key(i));
    }
    return keys;
  }

  /**
   * Get all keys from both localStorage and sessionStorage
   * @returns {Object} - Object with localStorageKeys and sessionStorageKeys arrays
   */
  static getAllStorageKeys() {
    const localStorageKeys = [];
    const sessionStorageKeys = [];

    // Get localStorage keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        localStorageKeys.push(key);
      }
    }

    // Get sessionStorage keys
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key) {
        sessionStorageKeys.push(key);
      }
    }

    return {
      localStorageKeys,
      sessionStorageKeys,
      allKeys: [...localStorageKeys, ...sessionStorageKeys],
      currentStorageKeys: this.getAllKeys() // Keys from current active storage
    };
  }

  /**
   * Get all key names as a simple array from current storage
   * @returns {string[]} - Array of key names
   */
  static getKeyNames() {
    return this.getAllKeys();
  }

  /**
   * Check if key exists
   */
  static hasKey(key) {
    if (this.isCacheValid(key)) {
      return true;
    }
    const storage = this.getStorage();
    return storage?.getItem(key) !== null;
  }

  /**
   * Get storage size
   */
  static getStorageSize() {
    let total = 0;
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        total += localStorage[key].length + key.length;
      }
    }
    for (let key in sessionStorage) {
      if (sessionStorage.hasOwnProperty(key)) {
        total += sessionStorage[key].length + key.length;
      }
    }
    return total;
  }

  /**
   * Get performance statistics
   */
  static getPerformanceStats() {
    const cacheHitRate = this.perfStats.cacheHits + this.perfStats.cacheMisses > 0
      ? (this.perfStats.cacheHits / (this.perfStats.cacheHits + this.perfStats.cacheMisses) * 100).toFixed(2)
      : 0;

    return {
      ...this.perfStats,
      cacheHitRate: `${cacheHitRate}%`,
      cacheSize: this.cache.size
    };
  }

  /**
   * Reset performance statistics
   */
  static resetPerformanceStats() {
    this.perfStats = {
      cacheHits: 0,
      cacheMisses: 0,
      encryptOps: 0,
      decryptOps: 0,
      writeOps: 0,
      readOps: 0
    };
  }

  /**
   * Get cache statistics
   */
  static getCacheStats() {
    this.clearExpiredCache();
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
      encrypted: this.shouldEncrypt(),
      usingSessionStorage: this.getStorage() === sessionStorage
    };
  }

  /**
   * Preload frequently accessed items
   */
  static preloadCache(keys) {
    this.getMultipleItems(keys);
  }

  /**
   * Migrate storage with improved performance
   */
  static migrateStorageOnRememberMeChange(newRememberMe) {
    const results = {
      migrated: [],
      failed: [],
      cleared: []
    };

    const sourceStorage = newRememberMe ? sessionStorage : localStorage;
    const targetStorage = newRememberMe ? localStorage : sessionStorage;

    const keys = [];
    for (let i = 0; i < sourceStorage.length; i++) {
      const key = sourceStorage.key(i);
      if (key !== 'rememberMe' && key !== '_FORCE_SESSION_') {
        keys.push(key);
      }
    }

    // Batch migration
    keys.forEach(key => {
      try {
        const value = sourceStorage.getItem(key);
        if (value !== null) {
          targetStorage.setItem(key, value);
          sourceStorage.removeItem(key);
          results.migrated.push(key);
        }
      } catch (error) {
        console.error(`Failed to migrate key ${key}:`, error);
        results.failed.push(key);
      }
    });

    if (!newRememberMe) {
      const localKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key !== 'rememberMe') {
          localKeys.push(key);
        }
      }
      localKeys.forEach(key => {
        localStorage.removeItem(key);
        results.cleared.push(key);
      });
    }

    this.clearCache();
    this.sendSyncSignal();

    return results;
  }

  /**
   * Send sync signal to other tabs
   */
  static sendSyncSignal() {
    localStorage.setItem('_storage_sync_signal_', Date.now().toString());
    setTimeout(() => {
      localStorage.removeItem('_storage_sync_signal_');
    }, 100);
  }

  /**
   * Get environment info
   */
  static getEnvironmentInfo() {
    return {
      server: CURRENT_SERVER,
      encrypted: this.shouldEncrypt(),
      cacheSize: this.cache.size,
      usingSessionStorage: this.getStorage() === sessionStorage,
      rememberMe: localStorage?.getItem('rememberMe')
    };
  }

  /**
   * Update rememberMe with optimized handling
   */
  static setRememberMe(remember, options = {}) {
    const {
      reloadPage = false,
      callback = null
    } = options;

    // Explicitly manage force session flag
    if (!remember) {
      sessionStorage.setItem('_FORCE_SESSION_', 'true');
    } else {
      sessionStorage.removeItem('_FORCE_SESSION_');
    }

    const currentRememberMe = localStorage?.getItem('rememberMe');
    const shouldRemember = currentRememberMe === 'true' || currentRememberMe === true;

    localStorage.setItem('rememberMe', String(remember));

    if (shouldRemember !== remember) {
      const result = this.migrateStorageOnRememberMeChange(remember);

      if (callback) {
        callback(result);
      }

      if (reloadPage && typeof window !== 'undefined') {
        setTimeout(() => {
          window.location.reload();
        }, 100);
      }

      return result;
    }

    return { migrated: [], failed: [], cleared: [] };
  }
}

// Optimized cleanup - runs less frequently
setInterval(() => {
  EncryptedStorage.clearExpiredCache();
}, 10 * 60 * 1000); // Every 10 minutes

export default EncryptedStorage;
